<script setup lang="ts">
import { computed, toRefs } from "vue";
import { useToast } from "vue-toast-notification";
import JsonTreeNode from "@/components/MyStudents/Submissions/components/SubmissionsInformation/components/JsonTreeView/JsonTreeNode.vue";
import CopyIcon from "@/components/shared/icons/CopyIcon.vue";
import type { DataVal } from "./types";
import { convertToDisplayNodes } from "./utils";

interface Props {
  rootKey?: string;
  data: Record<string, unknown>;
  dataTestid?: string;
  legacy?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  rootKey: "",
  dataTestid: "my-students-submission-current-jsontree",
  legacy: false,
});

const { rootKey, data, dataTestid, legacy } = toRefs(props);

const toast = useToast();

const parsed = computed<DataVal>(() => {
  if (data.value instanceof Object) {
    return convertToDisplayNodes(rootKey.value, { ...data.value });
  }
  return {
    name: rootKey.value,
    value: data.value,
    children: undefined,
  };
});

const displayNodesToDict = (node: DataVal): Record<string, unknown> => {
  if (!node.children || node.children.length === 0) {
    return {
      [node.name]: node.value,
    };
  }

  const childrenDict = {};

  node.children.forEach((child) => {
    Object.assign(childrenDict, displayNodesToDict(child));
  });

  return node.name ? { [node.name]: childrenDict } : childrenDict;
};

const handleCopy = (): void => {
  const dictNodes = displayNodesToDict(parsed.value);
  navigator.clipboard.writeText(JSON.stringify(dictNodes, null, 2)).then(() => {
    toast.info("Copied to clipboard!", {
      position: "top-right",
      duration: 2000,
    });
  });
};
</script>

<template>
  <div
    :class="legacy ? 'shadow-md rounded-lg p-4 text-indigo-base text-sm md:w-[22rem]' : 'bg-blue-400 rounded-lg px-5 py-4'"
    class="flex flex-col"
    :data-testid="dataTestid"
  >
    <div v-if="!legacy" class="flex items-center justify-between mb-2">
      <span class="text-xs font-bold text-gray-600 uppercase">Submission data</span>
      <button
        class="inline-flex items-center gap-[6px] px-[10px] py-[6px] border border-teal-900 rounded-[4px] bg-white text-teal-900 text-xs font-bold cursor-pointer transition-colors duration-[120ms] ease-in hover:bg-teal-900 hover:text-white"
        @click="handleCopy"
      >
        <CopyIcon />
        Copy
      </button>
    </div>
    <ul>
      <JsonTreeNode :node="parsed" :legacy="legacy" />
    </ul>
  </div>
</template>

<style scoped>
ul {
  list-style-type: none;
  margin-left: 0;
  padding: 0;
}
</style>
